import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { Container, Typography, Box, Grid, Card, CardContent, Button, List, ListItem, ListItemText, Alert, CircularProgress, RadioGroup, FormControlLabel, Radio, FormControl, Chip, Snackbar } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { getHostTenantSlug } from '../utils/tenantResolver';
import {
  getManageAccountCacheKey,
  readManageAccountCache,
} from '../components/manageAccount/manageAccountSessionCache';
import {
  ADDON_PRICING_CATALOG,
  buildStandardPlanFeatureBullets,
  ENTERPRISE_FEATURE_BULLETS,
  PLAN_PRICING_META,
} from '../config/planLimitsConfig';
import ContactSalesModal from '../components/ContactSalesModal';

type PricingSubscriptionInfo = {
  planType?: string;
  displayPlan?: string;
  status?: string;
  subscriptionStatus?: string;
  subscriptionBillingStatus?: string;
  billingCycle?: string;
  yearlyUpgradeOnly?: boolean;
  yearlyUpgradeOnlyMessage?: string;
  currentPeriodStart?: number;
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
  autopayEnabled?: boolean;
};

const YEARLY_UPGRADE_ONLY_PRICING_MESSAGE =
  'You currently have an active yearly subscription. Monthly plans will become available after your yearly subscription expires.';

const subscriptionInfoFromResponse = (res: Record<string, unknown> | null | undefined): PricingSubscriptionInfo | null => {
  if (!res) return null;
  return {
    planType: res.planType as string | undefined,
    displayPlan: res.displayPlan as string | undefined,
    status: res.status as string | undefined,
    subscriptionStatus: res.subscriptionStatus as string | undefined,
    subscriptionBillingStatus: res.subscriptionBillingStatus as string | undefined,
    billingCycle: res.billingCycle as string | undefined,
    yearlyUpgradeOnly: res.yearlyUpgradeOnly === true,
    yearlyUpgradeOnlyMessage: res.yearlyUpgradeOnlyMessage as string | undefined,
    currentPeriodStart: (res.currentPeriodStart ?? res.current_period_start) as number | undefined,
    currentPeriodEnd: (res.currentPeriodEnd ?? res.current_period_end) as number | undefined,
    cancelAtPeriodEnd: res.cancelAtPeriodEnd as boolean | undefined,
    autopayEnabled: res.autopayEnabled as boolean | undefined,
  };
};

const lastSubscribedPlanFromResponse = (res: Record<string, unknown> | null | undefined): string | null => {
  const plan = (res?.planType as string | undefined)?.toUpperCase() || null;
  return plan && (plan === 'STANDARD' || plan === 'PROFESSIONAL') ? plan : null;
};

const isActivePaidSubscription = (info: PricingSubscriptionInfo | null): boolean =>
  !!info &&
  (info.planType === 'STANDARD' || info.planType === 'PROFESSIONAL') &&
  info.displayPlan !== 'EXPIRED' &&
  info.displayPlan !== 'CANCELLED' &&
  (info.status === 'ACTIVE' ||
    info.status === 'active' ||
    info.subscriptionStatus === 'active');

const getUpgradeSubscriptionSeed = (upgradeMode: boolean) => {
  if (!upgradeMode) {
    return { lastSubscribedPlan: null as string | null, subscriptionInfo: null as PricingSubscriptionInfo | null };
  }
  const cacheKey = getManageAccountCacheKey(null, getHostTenantSlug());
  const cached = readManageAccountCache<{ subscription?: Record<string, unknown> }>('subscription', cacheKey);
  const sub = cached?.subscription;
  return {
    lastSubscribedPlan: lastSubscribedPlanFromResponse(sub),
    subscriptionInfo: subscriptionInfoFromResponse(sub),
  };
};

interface UpgradePreview {
  professionalPlanPrice?: number;
  prorationCredit?: number;
  finalPayableAmount?: number;
  /** Full new-plan price for the period (from backend day-based proration). */
  planPrice?: number;
  /** Net amount due after credit (from backend). */
  amountDue?: number;
  billingCycle?: string;
}

interface UpgradeResult {
  professionalPlanPrice?: number;
  prorationCredit?: number;
  finalChargedAmount?: number;
  billingCycle?: string;
  message?: string;
}

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  // If coming from in-app subscription (upgrade flow), show plans based on last subscribed plan
  const mode = searchParams.get('mode');
  const isUpgradeMode = mode === 'upgrade';
  const upgradeSubscriptionSeed = getUpgradeSubscriptionSeed(isUpgradeMode);

  // Last subscribed plan + subscription metadata from API (used in upgrade mode so visibility is correct after cancellation)
  const [lastSubscribedPlan, setLastSubscribedPlan] = useState<string | null>(upgradeSubscriptionSeed.lastSubscribedPlan);
  const [subscriptionInfo, setSubscriptionInfo] = useState<PricingSubscriptionInfo | null>(
    upgradeSubscriptionSeed.subscriptionInfo
  );

  const [subscriptionView, setSubscriptionView] = useState<{
    plansToShow?: string[];
    showCurrentPlan?: boolean;
    readOnly?: boolean;
    status?: string;
  } | null>(null);

  /** When /subscription/view fails, still hide Free for expired paid tenants (show Standard / Professional / Enterprise only). */
  const [paidOnlyForExpiredPricing, setPaidOnlyForExpiredPricing] = useState(false);

  useEffect(() => {
    if (!user) {
      setSubscriptionView(null);
      setPaidOnlyForExpiredPricing(false);
      return;
    }
    let cancelled = false;
    Promise.all([
      apiService.getSubscriptionView(),
      apiService.getSubscription().catch(() => null),
    ])
      .then(([v, sub]) => {
        if (cancelled) return;
        setSubscriptionView(v);
        setPaidOnlyForExpiredPricing(sub?.displayPlan === 'EXPIRED');
      })
      .catch(() => {
        if (!cancelled) {
          setSubscriptionView(null);
          setPaidOnlyForExpiredPricing(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!isUpgradeMode || !user) return;
    let cancelled = false;
    apiService.getSubscription()
      .then((res) => {
        if (cancelled) return;
        setLastSubscribedPlan(lastSubscribedPlanFromResponse(res));
        setSubscriptionInfo(subscriptionInfoFromResponse(res));
      })
      .catch(() => {
        if (!cancelled) {
          setLastSubscribedPlan(null);
          setSubscriptionInfo(null);
        }
      });
    return () => { cancelled = true; };
  }, [isUpgradeMode, user]);

  // Resolve plan for visibility: API (last subscribed) overrides context
  const planForVisibility = lastSubscribedPlan ?? user?.planType?.toUpperCase() ?? null;

  // Determine if there is an actually active paid plan (matches logic in Subscription.tsx)
  const userPaidPlanType = user?.planType?.toUpperCase();
  const hasActivePaidPlan =
    isActivePaidSubscription(subscriptionInfo) ||
    (isUpgradeMode &&
      subscriptionInfo === null &&
      (userPaidPlanType === 'STANDARD' || userPaidPlanType === 'PROFESSIONAL'));

  const yearlyUpgradeOnly = subscriptionInfo?.yearlyUpgradeOnly === true;

  useEffect(() => {
    if (yearlyUpgradeOnly) {
      setBillingCycle('yearly');
    }
  }, [yearlyUpgradeOnly]);

  // Which plan card is visually highlighted.
  const [selectedPlan, setSelectedPlan] = useState<string>(() => {
    if (isUpgradeMode && (lastSubscribedPlan || user?.planType)) {
      const currentPlan = (lastSubscribedPlan ?? user?.planType ?? '').toUpperCase();
      if (currentPlan === 'FREE' || !currentPlan) return 'Standard';
      if (currentPlan === 'STANDARD') return 'Professional';
      if (currentPlan === 'PROFESSIONAL') return 'Professional';
    }
    return 'Standard';
  });

  useEffect(() => {
    if (!isUpgradeMode || !planForVisibility) return;
    const p = planForVisibility.toUpperCase();
    if (p === 'STANDARD') setSelectedPlan('Professional');
    else if (p === 'PROFESSIONAL') setSelectedPlan('Professional');
  }, [isUpgradeMode, planForVisibility]);

  // Upgrade preview / confirmation state (STANDARD -> PROFESSIONAL, in upgrade mode only)
  const [upgradePreview, setUpgradePreview] = useState<UpgradePreview | null>(null);
  const [upgradePreviewLoading, setUpgradePreviewLoading] = useState(false);
  const [upgradePreviewError, setUpgradePreviewError] = useState<string | null>(null);
  const [upgradeResult, setUpgradeResult] = useState<UpgradeResult | null>(null);
  const [upgradeConfirming, setUpgradeConfirming] = useState(false);
  const [contactSalesOpen, setContactSalesOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const paymentClickStartedAtRef = useRef(0);
  const upgradeCheckoutSectionRef = useRef<HTMLDivElement | null>(null);
  const pendingUpgradePreviewScrollRef = useRef(false);

  const logPaymentButtonDisable = useCallback((source: string, clickAt: number) => {
    const disableMs = performance.now() - clickAt;
    console.info('[Pricing] payment button disable timing', {
      source,
      disableMs: Math.round(disableMs * 100) / 100,
    });
    return disableMs;
  }, []);

  const beginPlanPaymentLoading = useCallback((planName: string) => {
    const clickAt = performance.now();
    paymentClickStartedAtRef.current = clickAt;
    flushSync(() => {
      setLoading(planName);
      setError(null);
    });
    logPaymentButtonDisable(`plan-${planName}`, clickAt);
  }, [logPaymentButtonDisable]);

  const endPlanPaymentLoading = useCallback((failed = false) => {
    const release = () => setLoading(null);
    if (!failed) {
      release();
      return;
    }
    const elapsed = performance.now() - paymentClickStartedAtRef.current;
    window.setTimeout(release, Math.max(0, 2000 - elapsed));
  }, []);

  const beginUpgradeConfirmLoading = useCallback(() => {
    const clickAt = performance.now();
    paymentClickStartedAtRef.current = clickAt;
    flushSync(() => {
      setUpgradeConfirming(true);
      setUpgradePreviewError(null);
    });
    logPaymentButtonDisable('confirm-upgrade', clickAt);
  }, [logPaymentButtonDisable]);

  const endUpgradeConfirmLoading = useCallback((failed = false) => {
    const release = () => setUpgradeConfirming(false);
    if (!failed) {
      release();
      return;
    }
    const elapsed = performance.now() - paymentClickStartedAtRef.current;
    window.setTimeout(release, Math.max(0, 2000 - elapsed));
  }, []);

  const addOns = ADDON_PRICING_CATALOG;

  const freeMeta = PLAN_PRICING_META.FREE;
  const standardMeta = PLAN_PRICING_META.STANDARD;
  const professionalMeta = PLAN_PRICING_META.PROFESSIONAL;
  const enterpriseMeta = PLAN_PRICING_META.ENTERPRISE;

  const plans = [
    {
      name: freeMeta.name,
      price: freeMeta.price,
      period: freeMeta.period,
      features: buildStandardPlanFeatureBullets('FREE'),
      buttonText: 'Start Free Trial',
      buttonVariant: 'outlined' as const,
    },
    {
      name: standardMeta.name,
      monthlyPrice: standardMeta.monthlyPrice,
      yearlyPrice: standardMeta.yearlyPrice,
      yearlyMonthlyAmount: standardMeta.yearlyMonthlyAmount,
      yearlyTotal: standardMeta.yearlyTotal,
      price: standardMeta.price,
      period: standardMeta.period,
      periodYearly: standardMeta.periodYearly,
      features: buildStandardPlanFeatureBullets('STANDARD'),
      buttonText: 'Get Started',
      buttonVariant: 'outlined' as const,
    },
    {
      name: professionalMeta.name,
      monthlyPrice: professionalMeta.monthlyPrice,
      yearlyPrice: professionalMeta.yearlyPrice,
      yearlyMonthlyAmount: professionalMeta.yearlyMonthlyAmount,
      yearlyTotal: professionalMeta.yearlyTotal,
      price: professionalMeta.price,
      period: professionalMeta.period,
      periodYearly: professionalMeta.periodYearly,
      features: buildStandardPlanFeatureBullets('PROFESSIONAL'),
      buttonText: 'Get Started',
      buttonVariant: 'outlined' as const,
    },
    {
      name: enterpriseMeta.name,
      price: enterpriseMeta.price,
      period: enterpriseMeta.period,
      features: ENTERPRISE_FEATURE_BULLETS,
      buttonText: 'Contact Sales',
      buttonVariant: 'contained' as const,
    },
  ];

  const mapPlanCodes: Record<string, string> = {
    FREE: 'Free',
    STANDARD: 'Standard',
    PROFESSIONAL: 'Professional',
    ENTERPRISE: 'Enterprise',
  };
  const paidPricingPlanNames = new Set(['Standard', 'Professional', 'Enterprise']);
  // Hide Free only in in-app upgrade flow (expired paid tenants). Public /pricing always shows all plans.
  const restrictToPaidPlansOnly =
    isUpgradeMode && (Boolean(subscriptionView?.plansToShow?.length) || paidOnlyForExpiredPricing);
  const allowedPlanNames = restrictToPaidPlansOnly
    ? subscriptionView?.plansToShow?.length
      ? new Set(subscriptionView.plansToShow.map((p) => mapPlanCodes[p] || p))
      : paidPricingPlanNames
    : null;
  const plansForGrid = allowedPlanNames ? plans.filter((plan) => allowedPlanNames.has(plan.name)) : plans;

  const handleStartUpgradePreview = async () => {
    try {
      setUpgradePreviewLoading(true);
      setUpgradePreviewError(null);
      setUpgradeResult(null);

      const cycleParam = billingCycle.toUpperCase();
      const data = await apiService.previewPlanUpgrade(cycleParam);

      if (data.error) {
        setUpgradePreviewError(data.error);
        setUpgradePreview(null);
      } else {
        setUpgradePreview({
          professionalPlanPrice: data.professionalPlanPrice,
          prorationCredit: data.prorationCredit,
          finalPayableAmount: data.finalPayableAmount,
          planPrice: data.planPrice,
          amountDue: data.amountDue,
          billingCycle: data.billingCycle ?? cycleParam,
        });
      }
    } catch (err: any) {
      setUpgradePreviewError(err.response?.data?.error || 'Failed to load upgrade preview. Please try again.');
      setUpgradePreview(null);
    } finally {
      setUpgradePreviewLoading(false);
    }
  };

  const handleConfirmUpgrade = async () => {
    beginUpgradeConfirmLoading();
    try {
      const cycleParam = billingCycle.toUpperCase();
      const data = await apiService.confirmPlanUpgrade(cycleParam, window.location.origin);

      if (data.error) {
        setUpgradePreviewError(data.error);
        endUpgradeConfirmLoading(true);
      } else if (data.requiresPayment && (data.paymentUrl || data.checkoutUrl)) {
        window.location.href = data.paymentUrl || data.checkoutUrl || '';
      } else {
        setUpgradeResult({
          professionalPlanPrice: data.professionalPlanPrice,
          prorationCredit: data.prorationCredit,
          finalChargedAmount: data.finalChargedAmount,
          billingCycle: data.billingCycle ?? cycleParam,
          message: data.message,
        });
        endUpgradeConfirmLoading(false);
      }
    } catch (err: any) {
      setUpgradePreviewError(err.response?.data?.error || 'Failed to confirm upgrade. Please try again.');
      endUpgradeConfirmLoading(true);
    }
  };

  const isStandardToProfessionalUpgrade =
    isUpgradeMode && planForVisibility === 'STANDARD';

  // If the upgrade preview is currently shown, keep it in sync with the selected billing cycle.
  useEffect(() => {
    if (!isStandardToProfessionalUpgrade) return;
    if (upgradeResult) return; // don't auto-refresh once upgrade is completed
    if (!upgradePreview && !upgradePreviewError) return; // only refresh when preview UI is already active
    // Refresh proration preview for the newly selected cycle
    handleStartUpgradePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingCycle]);

  useEffect(() => {
    if (!pendingUpgradePreviewScrollRef.current) return;
    if (!isStandardToProfessionalUpgrade) return;
    if (!(upgradePreviewLoading || upgradePreview || upgradePreviewError || upgradeResult)) return;
    pendingUpgradePreviewScrollRef.current = false;
    window.requestAnimationFrame(() => {
      upgradeCheckoutSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [isStandardToProfessionalUpgrade, upgradePreviewLoading, upgradePreview, upgradePreviewError, upgradeResult]);

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null || Number.isNaN(value)) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const proratedProfessionalChargeForPreview = (preview: UpgradePreview) => {
    const amountDue = preview.amountDue ?? preview.finalPayableAmount ?? 0;
    const prorationCredit = Math.abs(preview.prorationCredit ?? 0);
    if (amountDue > 0 || prorationCredit > 0) {
      return amountDue + prorationCredit;
    }
    if (typeof preview.professionalPlanPrice === 'number') return preview.professionalPlanPrice;
    if (typeof preview.planPrice === 'number') return preview.planPrice;
    return 0;
  };

  const planThemeByName: Record<string, { accent: string; icon: string; badgeBg?: string; buttonBg?: string }> = {
    Free: {
      accent: 'linear-gradient(165deg, rgba(191,219,254,0.55) 0%, #ffffff 52%)',
      icon: '#3b82f6',
      buttonBg: 'linear-gradient(135deg, #1e3a8a 0%, #0f2f7d 100%)',
    },
    Standard: {
      accent: 'radial-gradient(circle at 0% 0%, rgba(74,222,128,0.22), transparent 55%)',
      icon: '#22c55e',
      badgeBg: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
      buttonBg: 'linear-gradient(135deg, #1e3a8a 0%, #0f2f7d 100%)',
    },
    Professional: {
      accent: 'radial-gradient(circle at 0% 0%, rgba(236,72,153,0.24), transparent 55%)',
      icon: '#ec4899',
      buttonBg: 'linear-gradient(135deg, #1e3a8a 0%, #0f2f7d 100%)',
    },
    Enterprise: {
      accent: 'radial-gradient(circle at 0% 0%, rgba(250,204,21,0.26), transparent 55%)',
      icon: '#eab308',
      badgeBg: 'linear-gradient(135deg, #1e3a8a 0%, #0f2f7d 100%)',
      buttonBg: 'linear-gradient(135deg, #1e3a8a 0%, #0f2f7d 100%)',
    },
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ py: 7 }}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          align="center"
          sx={{
            fontWeight: 700,
            color: '#0b2568',
            fontSize: { xs: '2.2rem', md: '3.3rem' },
            mb: 1.5,
          }}
        >
          {isUpgradeMode ? 'Upgrade your plan' : 'Choose Your Plan'}
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          align="center"
          paragraph
          sx={{ mb: 2.2 }}
        >
          {isUpgradeMode
            ? 'Upgrade from Standard to Professional with prorated billing.'
            : 'Select the plan that fits your needs'}
        </Typography>
        {!isUpgradeMode && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ maxWidth: 720, mx: 'auto', mb: 1 }}
          >
            API executions = each HTTP call CRUD Expert sends when running a test plan.
            One request with 3 data sets counts as 3 executions.
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Billing Cycle Selector for Standard and Professional Plans */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
          {yearlyUpgradeOnly && (
            <Alert severity="info" sx={{ mb: 2, maxWidth: 720, width: '100%' }}>
              {YEARLY_UPGRADE_ONLY_PRICING_MESSAGE}
            </Alert>
          )}
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={billingCycle}
              onChange={(e) => setBillingCycle(e.target.value as 'monthly' | 'yearly')}
              sx={{ justifyContent: 'center', alignItems: 'center', gap: 2 }}
            >
              <FormControlLabel
                value="monthly"
                disabled={yearlyUpgradeOnly}
                control={<Radio sx={{ color: '#0f2f7d', '&.Mui-checked': { color: '#0f2f7d' } }} />}
                label="Monthly"
              />
              <FormControlLabel
                value="yearly"
                control={<Radio sx={{ color: '#0f2f7d', '&.Mui-checked': { color: '#0f2f7d' } }} />}
                label="Yearly"
              />
              <Chip
                label="SAVE 15% Per Year"
                size="small"
                sx={{
                  height: 30,
                  borderRadius: 999,
                  bgcolor: '#dbeafe',
                  color: '#0f2f7d',
                  fontWeight: 700,
                }}
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Upgrade preview (Stripe-style) when in upgrade mode and preview is loaded */}
        {isStandardToProfessionalUpgrade && (upgradePreviewLoading || upgradePreview || upgradeResult || upgradePreviewError) && (
          <Box ref={upgradeCheckoutSectionRef} sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
            <Card sx={{ maxWidth: 720, width: '100%' }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Upgrade to Professional Plan
                </Typography>

                {/* Plan comparison */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Current plan
                    </Typography>
                    <Typography variant="h6">Standard</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {billingCycle === 'monthly' ? '$39 / month per user' : '$399 / year per user'}
                    </Typography>
                  </Box>
                  <Box sx={{ mx: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      →
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      New plan
                    </Typography>
                    <Typography variant="h6">Professional</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {billingCycle === 'monthly' ? '$79 / month per user' : '$799 / year per user'}
                    </Typography>
                  </Box>
                </Box>

                {upgradePreviewError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {upgradePreviewError}
                  </Alert>
                )}

                {!upgradeResult && (
                  <>
                    {/* Billing breakdown preview */}
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 2.5,
                        mb: 2,
                        backgroundColor: 'background.paper',
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Billing Summary
                      </Typography>
                      {upgradePreviewLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">
                            Calculating proration...
                          </Typography>
                        </Box>
                      )}
                      {!upgradePreviewLoading && upgradePreview && (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">
                              Professional plan (prorated for remaining period)
                            </Typography>
                            <Typography variant="body2">
                              {formatCurrency(proratedProfessionalChargeForPreview(upgradePreview))}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">
                              Proration Credit (remaining from current plan applied to new plan)
                            </Typography>
                            <Typography variant="body2">
                              -{formatCurrency(Math.abs(upgradePreview.prorationCredit ?? 0))}
                            </Typography>
                          </Box>
                          <Box sx={{ my: 1, borderTop: '1px solid', borderColor: 'divider' }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Amount Due Now
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {formatCurrency(upgradePreview.amountDue ?? upgradePreview.finalPayableAmount)}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      You are upgrading in the middle of your current plan. The previous remaining amount from your
                      current subscription is applied as credit toward the new plan (shown above as Proration Credit).
                      You only pay the upgrade cost (Amount Due Now).
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setUpgradePreview(null);
                          setUpgradePreviewError(null);
                          setUpgradeResult(null);
                          // Go back to Manage Account Subscription tab if in app, else just history back
                          if (isUpgradeMode) {
                            navigate('/manage-account?tab=subscription');
                          } else {
                            navigate(-1);
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={upgradePreviewLoading || !upgradePreview || upgradeConfirming}
                        onClick={handleConfirmUpgrade}
                      >
                        {upgradeConfirming ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                            Processing...
                          </>
                        ) : (
                          'Confirm Upgrade'
                        )}
                      </Button>
                    </Box>
                  </>
                )}

                {upgradeResult && (
                  <>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {upgradeResult.message || 'Plan upgraded successfully.'}
                    </Alert>
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 2.5,
                        mb: 2,
                        backgroundColor: 'background.paper',
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Invoice Summary
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Professional Plan Price</Typography>
                        <Typography variant="body2">{formatCurrency(upgradeResult.professionalPlanPrice)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Proration Credit</Typography>
                        <Typography variant="body2">
                          -{formatCurrency(Math.abs(upgradeResult.prorationCredit ?? 0))}
                        </Typography>
                      </Box>
                      <Box sx={{ my: 1, borderTop: '1px solid', borderColor: 'divider' }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Final Amount Charged
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {formatCurrency(upgradeResult.finalChargedAmount)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={() => navigate('/manage-account?tab=subscription')}
                      >
                        Back to Subscription
                      </Button>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* In upgrade mode, hide downgrade option when last plan was Professional */}
        {isUpgradeMode && planForVisibility === 'PROFESSIONAL' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            To downgrade to Standard, please contact support. Downgrade self-service will be available once billing rules are in place.
          </Alert>
        )}

        <Grid container spacing={4} sx={{ mt: 4 }}>
          {plansForGrid
            .filter(plan => {
              if (!isUpgradeMode) return true;
              // In upgrade mode: show plans based on last subscribed plan
              // Last plan = Professional → show Professional + Enterprise only
              // Last plan = Standard (or none) → show Standard + Professional + Enterprise
              if (plan.name === 'Enterprise') return true;
              if (plan.name === 'Professional') return true;
              if (plan.name === 'Standard' && planForVisibility === 'PROFESSIONAL') return false;
              return plan.name === 'Standard';
            })
            .map((plan) => {
            const currentUserPlan = planForVisibility;
            const planKey = plan.name.toUpperCase();
            const isCurrentPlan = !!(isUpgradeMode && hasActivePaidPlan && currentUserPlan && currentUserPlan === planKey);
            const buttonDisplayText = isCurrentPlan ? 'Current Plan' : plan.buttonText;

            return (
            <Grid item xs={12} sm={6} md={3} key={plan.name} sx={{ display: 'flex' }}>
              <Card 
                onClick={() => setSelectedPlan(plan.name)}
                sx={{ 
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: plan.name === selectedPlan ? 2 : 1,
                  borderColor: plan.name === selectedPlan ? '#0f2f7d' : '#e6ebf5',
                  boxShadow: plan.name === selectedPlan ? '0 14px 30px rgba(15,47,125,0.14)' : '0 4px 16px rgba(15,23,42,0.06)',
                  cursor: 'pointer',
                  borderRadius: 2,
                  transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
                  background: planThemeByName[plan.name]?.accent ?? '#ffffff',
                  '&:hover': {
                    boxShadow: '0 16px 30px rgba(15,47,125,0.12)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2.6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8, justifyContent: 'space-between' }}>
                    <Typography variant="h4" component="span" sx={{ color: '#0b2568', fontWeight: 700, fontSize: '2rem' }}>
                      {plan.name}
                    </Typography>
                    {isCurrentPlan && (
                      <Chip label="Current Plan" color="primary" size="small" />
                    )}
                    {!isCurrentPlan && plan.name === 'Standard' && (
                      <Chip
                        label="POPULAR"
                        size="small"
                        sx={{
                          color: '#ffffff',
                          fontWeight: 800,
                          letterSpacing: '0.02em',
                          bgcolor: '#16a34a',
                          border: '1px solid #15803d',
                          boxShadow: '0 2px 8px rgba(21, 128, 61, 0.28)',
                        }}
                      />
                    )}
                    {!isCurrentPlan && plan.name === 'Enterprise' && (
                      <Chip
                        label="BEST VALUE"
                        size="small"
                        sx={{
                          color: '#ffffff',
                          fontWeight: 800,
                          letterSpacing: '0.02em',
                          bgcolor: '#0f2f7d',
                          border: '1px solid #1e3a8a',
                          boxShadow: '0 2px 8px rgba(15, 47, 125, 0.3)',
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ my: 2 }}>
                    {(plan.name === 'Standard' || plan.name === 'Professional') ? (
                      billingCycle === 'monthly' ? (
                        <>
                          <Typography variant="h4" component="span" sx={{ color: '#0f172a', fontWeight: 700 }}>
                            {plan.monthlyPrice}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" component="span">
                            {` / ${plan.period}`}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="h4" component="span" sx={{ color: '#0f172a', fontWeight: 700 }}>
                            {`$${('yearlyTotal' in plan && plan.yearlyTotal) ? plan.yearlyTotal : ((plan.yearlyMonthlyAmount || 0) * 12).toFixed(2)}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" component="span">
                            {' / year per user'}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {`(${plan.yearlyPrice} ${plan.periodYearly || plan.period})`}
                          </Typography>
                        </>
                      )
                    ) : plan.name === 'Free' ? (
                      billingCycle === 'monthly' ? (
                        <>
                          <Typography variant="h4" component="span" sx={{ color: '#0f172a', fontWeight: 700 }}>
                            {plan.price}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" component="span">
                            {` / ${plan.period}`}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="h4" component="span" sx={{ color: '#0f172a', fontWeight: 700 }}>
                            {plan.price}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" component="span">
                            {' / year per user'}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            (always free / 1 user)
                          </Typography>
                        </>
                      )
                    ) : (
                      <>
                        <Typography variant="h4" component="span" sx={{ color: '#0f172a', fontWeight: 700 }}>
                          {plan.price}
                        </Typography>
                        {plan.period !== 'forever' && plan.period !== 'pricing' && plan.period !== 'for 14 days' && (
                          <Typography variant="body2" color="text.secondary" component="span">
                            {` / ${plan.period}`}
                          </Typography>
                        )}
                        {(plan.period === 'forever' || plan.period === 'for 14 days' || plan.period === 'pricing') && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {plan.period}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                  <List dense sx={{ mt: 0.5 }}>
                    {plan.features.map((feature, index) => (
                      <ListItem key={index} disablePadding>
                        <CheckIcon sx={{ mr: 1, fontSize: 20, color: '#0f2f7d' }} />
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ sx: { color: '#1e293b', fontSize: '0.99rem' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <Box sx={{ p: 2 }}>
                  <Button
                    fullWidth
                    variant={isCurrentPlan ? 'outlined' : plan.buttonVariant}
                    size="large"
                    disabled={loading === plan.name || isCurrentPlan}
                    onClick={async () => {
                      if (isCurrentPlan) return;
                      if (plan.name === 'Enterprise') {
                        setSelectedPlan(plan.name);
                        setContactSalesOpen(true);
                      } else if (plan.name === 'Free') {
                        setSelectedPlan(plan.name);
                        navigate('/signup');
                      } else if (plan.name === 'Standard' || plan.name === 'Professional') {
                        flushSync(() => setSelectedPlan(plan.name));
                        beginPlanPaymentLoading(plan.name);

                        try {
                          if (!isUpgradeMode) {
                            navigate(`/signup?plan=${plan.name.toUpperCase()}&billingCycle=${billingCycle}`);
                            endPlanPaymentLoading(false);
                            return;
                          }

                          let organizationId: number | undefined;
                          if (user?.organizationId) {
                            organizationId = user.organizationId;
                          }

                          if (!user || !organizationId) {
                            setError('You must be logged in with an organization to upgrade your plan.');
                            endPlanPaymentLoading(true);
                            return;
                          }

                          if (isUpgradeMode && isStandardToProfessionalUpgrade && plan.name === 'Professional') {
                            pendingUpgradePreviewScrollRef.current = true;
                            await handleStartUpgradePreview();
                            endPlanPaymentLoading(false);
                            return;
                          }

                          navigate(`/payment?plan=${plan.name.toUpperCase()}&orgId=${organizationId}&billingCycle=${billingCycle}`);
                        } catch (err: any) {
                          setError(err.response?.data?.message || 'Failed to start payment process');
                          endPlanPaymentLoading(true);
                        }
                      }
                    }}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 700,
                      color: '#ffffff',
                      backgroundColor: '#0f2f7d',
                      borderColor: '#0f2f7d',
                      '&:hover': {
                        backgroundColor: '#0b2568',
                        borderColor: '#0b2568',
                      },
                      '&.Mui-disabled': {
                        color: '#ffffff',
                        backgroundColor: '#93a7d8',
                        borderColor: '#93a7d8',
                      },
                    }}
                  >
                    {loading === plan.name ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Processing...
                      </>
                    ) : (
                      buttonDisplayText
                    )}
                  </Button>
                </Box>
              </Card>
            </Grid>
          );
          })}
        </Grid>
        {!isUpgradeMode && (
          <>
            <Box sx={{ mt: 8, mb: 3 }}>
              <Typography
                variant="h4"
                component="h2"
                align="center"
                sx={{ fontWeight: 700, color: '#0b2568', mb: 1 }}
              >
                Add-ons
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
                Extend any paid plan without upgrading. Available from Manage Account after subscription.
              </Typography>
              <Grid container spacing={3}>
                {addOns.map((addOn) => (
                  <Grid item xs={12} sm={6} md={3} key={addOn.name}>
                    <Card
                      sx={{
                        height: '100%',
                        border: '1px solid #e6ebf5',
                        borderRadius: 2,
                        boxShadow: '0 4px 16px rgba(15,23,42,0.06)',
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0b2568', mb: 1 }}>
                          {addOn.name}
                        </Typography>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="h5" component="span" sx={{ fontWeight: 700, color: '#0f172a' }}>
                            {addOn.price}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" component="span">
                            {` / ${addOn.period}`}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {addOn.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/signup')}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 5,
                  py: 1.15,
                  fontWeight: 700,
                  fontSize: '1.8rem',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #0f2f7d 100%)',
                  boxShadow: '0 8px 18px rgba(15,47,125,0.32)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2347a8 0%, #133b95 100%)',
                  },
                }}
              >
                Get Started Today
              </Button>
            </Box>
          </>
        )}
      </Box>
      <ContactSalesModal
        open={contactSalesOpen}
        onClose={() => setContactSalesOpen(false)}
        onSuccess={() =>
          setSnackbar({
            open: true,
            message: 'Thank you. Our sales team will contact you shortly.',
            severity: 'success',
          })
        }
        onError={(message) =>
          setSnackbar({
            open: true,
            message,
            severity: 'error',
          })
        }
        defaultEmail={user?.email || ''}
        defaultName={user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : ''}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Pricing;

